 import React from 'react';
import { Col, Form, Row } from '@/components/ui';
import type {
  HiredEquipmentEntryMethod,
  TaskOption,
  VendorOption,
} from '../types';
import type { HiredEquipmentPurchaseOrder } from '../types';
import { formatPoOptionLabel } from '../utils/poDisplay';
import HiredEquipmentTaskPicker from './HiredEquipmentTaskPicker';

export interface EntryMethodFieldsProps {
  entryMethod: HiredEquipmentEntryMethod;
  poEnabled: boolean;
  vendors: VendorOption[];
  vendorsLoading: boolean;
  vendorId: number | '';
  onVendorChange: (id: number | '') => void;
  purchaseOrders: HiredEquipmentPurchaseOrder[];
  posLoading: boolean;
  poId: number | '';
  onPoChange: (id: number | '') => void;
  selectedTask: TaskOption | null;
  onTaskChange: (task: TaskOption | null) => void;
  fetchTasks: (query: string) => Promise<TaskOption[]>;
  prePopulate: boolean;
  onPrePopulateChange: (checked: boolean) => void;
}

const RequiredMark: React.FC = () => <span className="tw-text-danger">*</span>;

const EntryMethodFields: React.FC<EntryMethodFieldsProps> = ({
  entryMethod,
  poEnabled,
  vendors,
  vendorsLoading,
  vendorId,
  onVendorChange,
  purchaseOrders,
  posLoading,
  poId,
  onPoChange,
  selectedTask,
  onTaskChange,
  fetchTasks,
  prePopulate,
  onPrePopulateChange,
}) => {
  const vendorSelect = (
    <Form.Group controlId="he-vendor">
      <Form.Label>
        Subcontractor / Vendor <RequiredMark />
      </Form.Label>
      <Form.Select
        value={vendorId === '' ? '' : String(vendorId)}
        disabled={vendorsLoading}
        onChange={(e) => {
          const next = e.target.value;
          onVendorChange(next ? Number(next) : '');
        }}
      >
        <option value="">{vendorsLoading ? 'Loading vendors…' : 'Select vendor...'}</option>
        {vendors.map((vendor) => (
          <option key={vendor.id} value={vendor.id}>
            {vendor.name}
          </option>
        ))}
      </Form.Select>
      <Form.Text>Vendor first — narrows the PO list.</Form.Text>
    </Form.Group>
  );

  const selectedPo = typeof poId === 'number' ? purchaseOrders.find((po) => po.id === poId) : undefined;
  const selectedPoDescription = selectedPo?.description?.trim() || '';

  const poSelect = (
    <Form.Group controlId="he-po">
      <Form.Label>
        Purchase Order {poEnabled ? <RequiredMark /> : null}
      </Form.Label>
      <Form.Select
        value={poId === '' ? '' : String(poId)}
        disabled={!vendorId || posLoading || !poEnabled}
        onChange={(e) => {
          const next = e.target.value;
          onPoChange(next ? Number(next) : '');
        }}
      >
        <option value="">
          {!vendorId
            ? 'Pick a vendor first'
            : posLoading
              ? 'Loading POs…'
              : !poEnabled
                ? 'POs not enabled — add lines manually'
                : purchaseOrders.length === 0
                  ? 'No active POs for this vendor'
                  : 'Select purchase order...'}
        </option>
        {purchaseOrders.map((po) => (
          <option key={po.id} value={po.id}>
            {formatPoOptionLabel(po)}
          </option>
        ))}
      </Form.Select>
      {selectedPoDescription ? (
        <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-content">{selectedPoDescription}</p>
      ) : (
        <Form.Text>
          {poEnabled ? 'Active POs for the selected vendor.' : "POs aren't enabled — this field is optional."}
        </Form.Text>
      )}
    </Form.Group>
  );

  const taskSelect = (
    <Form.Group controlId="he-task">
      <Form.Label>
        Task <RequiredMark />
      </Form.Label>
      <HiredEquipmentTaskPicker
        selected={selectedTask}
        onChange={onTaskChange}
        searchTasks={fetchTasks}
      />
      <Form.Text>Search by task number or description. The project is shown when the task has one.</Form.Text>
    </Form.Group>
  );

  if (entryMethod === 'purchase_order') {
    return (
      <>
        <Row>
          <Col md={6}>{vendorSelect}</Col>
          <Col md={6}>{poSelect}</Col>
        </Row>
        <Form.Check
          id="he-prepopulate"
          checked={prePopulate}
          disabled={!poEnabled}
          onChange={(e) => onPrePopulateChange(e.currentTarget.checked)}
          label="Pre-populate the grid with this PO's resources (recommended)"
        />
      </>
    );
  }

  if (entryMethod === 'subcontractor') {
    return (
      <Row>
        <Col md={6}>{vendorSelect}</Col>
      </Row>
    );
  }

  return (
    <Row>
      <Col md={6}>{taskSelect}</Col>
    </Row>
  );
};

export default EntryMethodFields;
